import { ApiError, fetchJson } from "./api";

export type CompanyWorkspaceTier = "portfolio" | "active_bench" | "universe";
export type CompanyWorkspaceSignalState = "ok" | "watching" | "tripped";
export type CompanyWorkspaceOperationalStatus =
  | "untested"
  | "ready"
  | "waiting"
  | "pending"
  | "evaluating"
  | "error"
  | "paused"
  | "archived";

export interface CompanyWorkspaceIdentity {
  issuer_id: string | null;
  canonical_ticker: string | null;
  display_ticker: string | null;
  company_name: string;
  exchange: string | null;
  mic: string | null;
  country: string | null;
  currency: string | null;
  security_type: string | null;
  sector: string | null;
  industry: string | null;
  tracked: boolean;
  directory_source: string | null;
}

export interface CompanyWorkspaceProviderStatus {
  as_of: string | null;
  caveat: string;
}

export interface CompanyWorkspaceCoverage {
  tier: CompanyWorkspaceTier;
  status: string;
  priority: string;
  owner: string | null;
  rationale: string | null;
  updated_at: string | null;
}

export interface CompanyWorkspaceDocument {
  document_id: string;
  title: string | null;
  source_name: string | null;
  provider: string | null;
  doc_type: string | null;
  document_date: string | null;
  ingested_at: string | null;
  page_count: number | null;
  deep_link: string;
}

export interface CompanyWorkspaceResearchArtifact {
  artifact_id: string;
  artifact_type: string;
  status: string;
  title: string | null;
  approved_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  deep_link: string;
}

export interface CompanyWorkspaceResearchSeries {
  artifact_type: string;
  latest_artifact: CompanyWorkspaceResearchArtifact;
  latest_approved_artifact: CompanyWorkspaceResearchArtifact | null;
  latest_requires_review: boolean;
  run_count: number;
}

export interface CompanyWorkspaceGap {
  gap_id: string;
  gap_text: string;
  gap_category: string | null;
  severity: string;
  status: string;
  updated_at: string | null;
  needs_attention: boolean;
}

export interface CompanyWorkspaceChallenger {
  relationship_id: string;
  relationship_type: string | null;
  rank: number | null;
  status: string;
  rationale: string | null;
  counterparty: {
    ticker: string | null;
    company_name: string;
    company_url: string | null;
  };
}

export interface CompanyWorkspaceMonitor {
  monitor_id: string;
  name: string;
  monitor_kind: "thesis_killer" | "key_signal";
  signal_state: CompanyWorkspaceSignalState | null;
  operational_status: CompanyWorkspaceOperationalStatus;
  signal_summary: string | null;
  last_evaluated_at: string | null;
  last_attempt_outcome: string | null;
  rule_needs_evaluation: boolean;
  needs_attention: boolean;
  deep_link: string;
}

export interface CompanyWorkspacePosition {
  book_name: string | null;
  side: "long" | "short" | "cash" | "other";
  status: string;
  weight_bps: number | null;
  target_weight_bps: number | null;
  updated_at: string | null;
}

export interface CompanyWorkspaceCollection<T> {
  items: T[];
  shown_count: number;
  total_count: number;
  limit: number;
}

export interface CompanyWorkspaceSectionState {
  authorized: boolean;
  available: boolean;
  included: boolean;
}

export interface CompanyWorkspaceAttentionSummary {
  high_priority_gap_count: number;
  monitor_signal_count: number;
  monitor_setup_or_data_issue_count: number;
  research_review_count: number;
  total_count: number;
}

export interface CompanyWorkspaceResponse {
  status: "ok";
  generated_at: string;
  company: CompanyWorkspaceIdentity;
  provider_status: CompanyWorkspaceProviderStatus;
  coverage: CompanyWorkspaceCoverage | null;
  documents: CompanyWorkspaceCollection<CompanyWorkspaceDocument>;
  research: CompanyWorkspaceCollection<CompanyWorkspaceResearchSeries>;
  gaps: CompanyWorkspaceCollection<CompanyWorkspaceGap>;
  challengers: CompanyWorkspaceCollection<CompanyWorkspaceChallenger>;
  attention: CompanyWorkspaceAttentionSummary;
  sections: {
    coverage: CompanyWorkspaceSectionState;
    documents: CompanyWorkspaceSectionState;
    research: CompanyWorkspaceSectionState;
    gaps: CompanyWorkspaceSectionState;
    challengers: CompanyWorkspaceSectionState;
    monitors: CompanyWorkspaceSectionState;
    position: CompanyWorkspaceSectionState;
  };
  monitors?: CompanyWorkspaceCollection<CompanyWorkspaceMonitor>;
  position?: CompanyWorkspacePosition | null;
}

export interface CompanyWorkspaceTierMutationResponse {
  status: "ok";
  issuer_id: string;
  ticker: string | null;
  tier: {
    tier: CompanyWorkspaceTier;
    status: string;
  } | null;
}

export interface CompanyWorkspaceGapMutationResponse {
  status: "ok";
  issuer_id: string;
  ticker: string;
  evidence_gap_updates: { updated: number; requested: number };
}

const now = new Date();
const isoDaysAgo = (days: number) => new Date(now.getTime() - days * 86_400_000).toISOString();

const DEV_WORKSPACE_SEED: CompanyWorkspaceResponse[] = [
  {
    status: "ok",
    generated_at: now.toISOString(),
    company: {
      issuer_id: "11111111-1111-4111-8111-111111111111",
      canonical_ticker: "MU",
      display_ticker: "MU",
      company_name: "Micron Technology",
      exchange: "NASDAQ",
      mic: "XNAS",
      country: "US",
      currency: "USD",
      security_type: "common_equity",
      sector: "Technology",
      industry: "Semiconductors",
      tracked: true,
      directory_source: "athena",
    },
    provider_status: {
      as_of: isoDaysAgo(1),
      caveat: "Canonical Athena identity; live market data is not included.",
    },
    coverage: {
      tier: "portfolio",
      status: "active",
      priority: "high",
      owner: "Semiconductors team",
      rationale: "Core memory and HBM exposure",
      updated_at: isoDaysAgo(8),
    },
    documents: {
      shown_count: 3,
      total_count: 124,
      limit: 5,
      items: [
        {
          document_id: "doc-dev-mu-1",
          title: "HBM pricing and demand update",
          source_name: "JPMorgan Research",
          provider: "jpmorgan",
          doc_type: "broker_note",
          document_date: isoDaysAgo(1).slice(0, 10),
          ingested_at: isoDaysAgo(1),
          page_count: 17,
          deep_link: "/app/documents/doc-dev-mu-1",
        },
        {
          document_id: "doc-dev-mu-2",
          title: "Quarterly results and prepared remarks",
          source_name: "Micron investor relations",
          provider: "company_ir",
          doc_type: "earnings_release",
          document_date: isoDaysAgo(5).slice(0, 10),
          ingested_at: isoDaysAgo(5),
          page_count: 24,
          deep_link: "/app/documents/doc-dev-mu-2",
        },
        {
          document_id: "doc-dev-mu-3",
          title: "Form 10-Q",
          source_name: "SEC",
          provider: "sec",
          doc_type: "regulatory_filing",
          document_date: isoDaysAgo(9).slice(0, 10),
          ingested_at: isoDaysAgo(9),
          page_count: 86,
          deep_link: "/app/documents/doc-dev-mu-3",
        },
      ],
    },
    research: {
      shown_count: 2,
      total_count: 2,
      limit: 6,
      items: [
        {
          artifact_type: "ic_packet",
          latest_requires_review: true,
          run_count: 12,
          latest_artifact: {
            artifact_id: "memo-dev-mu-latest",
            artifact_type: "ic_packet",
            status: "review_required",
            title: "MU re-underwrite packet — HBM demand update",
            approved_at: null,
            created_at: isoDaysAgo(1),
            updated_at: isoDaysAgo(1),
            deep_link: "/app/research/memo-dev-mu-latest",
          },
          latest_approved_artifact: {
            artifact_id: "memo-dev-mu-approved",
            artifact_type: "ic_packet",
            status: "approved",
            title: "MU investment committee packet — prior approved view",
            approved_at: isoDaysAgo(31),
            created_at: isoDaysAgo(33),
            updated_at: isoDaysAgo(31),
            deep_link: "/app/research/memo-dev-mu-approved",
          },
        },
        {
          artifact_type: "counter_thesis",
          latest_requires_review: false,
          run_count: 4,
          latest_artifact: {
            artifact_id: "memo-dev-mu-counter",
            artifact_type: "counter_thesis",
            status: "approved",
            title: "MU counter-thesis review",
            approved_at: isoDaysAgo(14),
            created_at: isoDaysAgo(15),
            updated_at: isoDaysAgo(14),
            deep_link: "/app/research/memo-dev-mu-counter",
          },
          latest_approved_artifact: {
            artifact_id: "memo-dev-mu-counter",
            artifact_type: "counter_thesis",
            status: "approved",
            title: "MU counter-thesis review",
            approved_at: isoDaysAgo(14),
            created_at: isoDaysAgo(15),
            updated_at: isoDaysAgo(14),
            deep_link: "/app/research/memo-dev-mu-counter",
          },
        },
      ],
    },
    gaps: {
      shown_count: 2,
      total_count: 2,
      limit: 8,
      items: [
        {
          gap_id: "gap-dev-mu-1",
          gap_text: "Corroborate HBM pricing outside company guidance",
          gap_category: "pricing",
          severity: "high",
          status: "open",
          updated_at: isoDaysAgo(2),
          needs_attention: true,
        },
        {
          gap_id: "gap-dev-mu-2",
          gap_text: "Update China revenue exposure after the latest filing",
          gap_category: "geography",
          severity: "medium",
          status: "open",
          updated_at: isoDaysAgo(5),
          needs_attention: false,
        },
      ],
    },
    challengers: {
      shown_count: 1,
      total_count: 1,
      limit: 8,
      items: [{
        relationship_id: "challenger-dev-mu-1",
        relationship_type: "direct_competitor",
        rank: 1,
        status: "active",
        rationale: "Closest public HBM capacity comparison",
        counterparty: {
          ticker: "000660.KS",
          company_name: "SK Hynix",
          company_url: "/app/companies/000660.KS",
        },
      }],
    },
    attention: {
      high_priority_gap_count: 1,
      monitor_signal_count: 1,
      monitor_setup_or_data_issue_count: 0,
      research_review_count: 1,
      total_count: 3,
    },
    sections: {
      coverage: { authorized: true, available: true, included: true },
      documents: { authorized: true, available: true, included: true },
      research: { authorized: true, available: true, included: true },
      gaps: { authorized: true, available: true, included: true },
      challengers: { authorized: true, available: true, included: true },
      monitors: { authorized: true, available: true, included: true },
      position: { authorized: true, available: true, included: true },
    },
    monitors: {
      shown_count: 2,
      total_count: 2,
      limit: 8,
      items: [
        {
          monitor_id: "monitor-dev-mu-1",
          name: "HBM price premium holds",
          monitor_kind: "thesis_killer",
          signal_state: "watching",
          operational_status: "ready",
          signal_summary: "Two recent sources put the price-premium assumption under review.",
          last_evaluated_at: isoDaysAgo(1),
          last_attempt_outcome: "evaluated",
          rule_needs_evaluation: false,
          needs_attention: true,
          deep_link: "/app/monitors?view=all&monitor=monitor-dev-mu-1",
        },
        {
          monitor_id: "monitor-dev-mu-2",
          name: "Gross margin floor",
          monitor_kind: "key_signal",
          signal_state: "ok",
          operational_status: "ready",
          signal_summary: "The latest stored fact satisfies the passing condition.",
          last_evaluated_at: isoDaysAgo(5),
          last_attempt_outcome: "evaluated",
          rule_needs_evaluation: false,
          needs_attention: false,
          deep_link: "/app/monitors?view=all&monitor=monitor-dev-mu-2",
        },
      ],
    },
    position: {
      book_name: "Core portfolio",
      side: "long",
      status: "active",
      weight_bps: 425,
      target_weight_bps: 500,
      updated_at: isoDaysAgo(3),
    },
  },
  {
    status: "ok",
    generated_at: now.toISOString(),
    company: {
      issuer_id: null,
      canonical_ticker: "LW",
      display_ticker: "LW",
      company_name: "Lamb Weston Holdings",
      exchange: "NYSE",
      mic: "XNYS",
      country: "US",
      currency: "USD",
      security_type: "equity",
      sector: "Consumer Defensive",
      industry: "Packaged Foods",
      tracked: false,
      directory_source: "fmp",
    },
    provider_status: {
      as_of: now.toISOString(),
      caveat: "Provider directory profile; not yet tracked in Athena.",
    },
    coverage: null,
    documents: { items: [], shown_count: 0, total_count: 0, limit: 5 },
    research: { items: [], shown_count: 0, total_count: 0, limit: 6 },
    gaps: { items: [], shown_count: 0, total_count: 0, limit: 8 },
    challengers: { items: [], shown_count: 0, total_count: 0, limit: 8 },
    attention: {
      high_priority_gap_count: 0,
      monitor_signal_count: 0,
      monitor_setup_or_data_issue_count: 0,
      research_review_count: 0,
      total_count: 0,
    },
    sections: {
      coverage: { authorized: true, available: false, included: false },
      documents: { authorized: true, available: false, included: false },
      research: { authorized: true, available: false, included: false },
      gaps: { authorized: true, available: false, included: false },
      challengers: { authorized: true, available: false, included: false },
      monitors: { authorized: true, available: false, included: false },
      position: { authorized: true, available: false, included: false },
    },
  },
];

let devWorkspaces = structuredClone(DEV_WORKSPACE_SEED);

function clone<T>(value: T): T {
  return structuredClone(value);
}

function retryableDevError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === "AbortError") return false;
  return !(error instanceof ApiError) || [404, 500, 502, 503, 504].includes(error.status);
}

function findDevWorkspace(identifier: string): CompanyWorkspaceResponse | undefined {
  const normalized = identifier.trim().toUpperCase();
  return devWorkspaces.find((workspace) =>
    workspace.company.issuer_id?.toUpperCase() === normalized
    || workspace.company.canonical_ticker?.toUpperCase() === normalized
    || workspace.company.display_ticker?.toUpperCase() === normalized,
  );
}

async function withDevFallback<T>(real: () => Promise<T>, fallback: () => T): Promise<T> {
  if (!import.meta.env.DEV) return real();
  try {
    return await real();
  } catch (error) {
    if (!retryableDevError(error)) throw error;
    return fallback();
  }
}

export function fetchCompanyWorkspace(
  identifier: string,
  signal?: AbortSignal,
): Promise<CompanyWorkspaceResponse> {
  const normalized = identifier.trim();
  const real = () => fetchJson<CompanyWorkspaceResponse>(
    `/api/portal/companies/${encodeURIComponent(normalized)}/workspace`,
    { signal },
  );
  const fixture = findDevWorkspace(normalized);
  if (fixture) return withDevFallback(real, () => clone(fixture));
  if (!import.meta.env.DEV) return real();
  return real().catch((error: unknown) => {
    if (!retryableDevError(error)) throw error;
    throw new ApiError(404, "Company not found");
  });
}

export function updateCompanyWorkspaceTier(
  ticker: string,
  body: { issuer_id: string; tier: CompanyWorkspaceTier },
): Promise<CompanyWorkspaceTierMutationResponse> {
  const normalizedTicker = ticker.trim().toUpperCase();
  const real = () => fetchJson<CompanyWorkspaceTierMutationResponse>(
    `/api/portal/companies/${encodeURIComponent(normalizedTicker)}/tier`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  return withDevFallback(real, () => {
    const workspace = findDevWorkspace(normalizedTicker);
    if (!workspace || workspace.company.issuer_id !== body.issuer_id || !workspace.company.tracked) {
      throw new ApiError(422, "Selected issuer and ticker are not an active company pair");
    }
    const previousTier = workspace.coverage?.tier ?? null;
    workspace.coverage = {
      tier: body.tier,
      status: workspace.coverage?.status ?? "active",
      priority: workspace.coverage?.priority ?? "normal",
      owner: workspace.coverage?.owner ?? null,
      rationale: workspace.coverage?.rationale ?? null,
      updated_at: new Date().toISOString(),
    };
    if (body.tier === "portfolio" && previousTier !== "portfolio" && !workspace.position) {
      workspace.position = {
        book_name: "Core portfolio",
        side: "long",
        status: "active",
        weight_bps: null,
        target_weight_bps: null,
        updated_at: new Date().toISOString(),
      };
    } else if (previousTier === "portfolio" && body.tier !== "portfolio") {
      workspace.position = null;
    }
    return {
      status: "ok",
      issuer_id: body.issuer_id,
      ticker: normalizedTicker,
      tier: workspace.coverage
        ? { tier: workspace.coverage.tier, status: workspace.coverage.status }
        : null,
    };
  });
}

export function updateCompanyWorkspaceGap(
  ticker: string,
  body: { issuer_id: string; gaps: Array<{ gap_id: string; status: "resolved" | "dismissed" }> },
): Promise<CompanyWorkspaceGapMutationResponse> {
  const normalizedTicker = ticker.trim().toUpperCase();
  const real = () => fetchJson<CompanyWorkspaceGapMutationResponse>(
    `/api/portal/companies/${encodeURIComponent(normalizedTicker)}/evidence-gaps`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  return withDevFallback(real, () => {
    const workspace = findDevWorkspace(normalizedTicker);
    if (!workspace || workspace.company.issuer_id !== body.issuer_id || !workspace.company.tracked) {
      throw new ApiError(422, "Selected issuer and ticker are not an active company pair");
    }
    const requestedIds = new Set(body.gaps.map((gap) => gap.gap_id));
    const removedAttention = workspace.gaps.items.filter(
      (gap) => requestedIds.has(gap.gap_id) && gap.needs_attention,
    ).length;
    const previousCount = workspace.gaps.items.length;
    workspace.gaps.items = workspace.gaps.items.filter((gap) => !requestedIds.has(gap.gap_id));
    const updated = previousCount - workspace.gaps.items.length;
    workspace.gaps.shown_count = workspace.gaps.items.length;
    workspace.gaps.total_count = Math.max(0, workspace.gaps.total_count - updated);
    workspace.attention.high_priority_gap_count = Math.max(
      0,
      workspace.attention.high_priority_gap_count - removedAttention,
    );
    workspace.attention.total_count = Math.max(
      0,
      workspace.attention.total_count - removedAttention,
    );
    return {
      status: "ok",
      issuer_id: body.issuer_id,
      ticker: normalizedTicker,
      evidence_gap_updates: { updated, requested: body.gaps.length },
    };
  });
}

export function resetCompanyWorkspaceDevFixtures(): void {
  devWorkspaces = structuredClone(DEV_WORKSPACE_SEED);
}
